import { endOfDay } from 'date-fns';
import { useEffect, useState } from 'react';
import { useAppContext } from 'src/contexts/AppContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { Content } from 'src/contexts/ContentContext';
import { DateRangeSelector } from 'src/core/POS/DateRangeSelector';
import { PosSpinner } from 'src/core/POS/PosSpinner';
import { Button } from 'src/core/ui/Button';
import { ContentId } from 'src/utils/constants/contentId';
import { StandardDateRangePresetNames } from 'src/utils/dateTimeUtils';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  DateTimeRangeWithRelative,
  SellerAccountClient,
} from 'src/WebApiController';

import * as styles from './AccountSetting.css';

export const RevealSyncDateInput = () => {
  const { activeAccountWebClientConfig } = useAppContext();
  const [overrideDate, setOverrideDate] =
    useState<DateTimeRangeWithRelative | null>({
      relativeEndSecs: null,
      relativeStartSecs: null,
      roundingMode: null,
      start: null,
      end: null,
    });
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const { showErrorDialog } = useClientLogContext();

  useEffect(() => {
    const fetchConfig = async () => {
      setIsFetching(true);
      await tryInvokeApi(
        async () => {
          const res = await new SellerAccountClient(
            activeAccountWebClientConfig
          ).getRevealSyncConfig();
          setIsFetching(false);
          setOverrideDate({
            relativeEndSecs:
              res?.overrideRevealSyncDateRelativeEndSecs ??
              (res?.overrideRevealSyncDateRelativeStartSecs ? 0 : null),
            relativeStartSecs:
              res?.overrideRevealSyncDateRelativeStartSecs ??
              (res?.overrideRevealSyncDateRelativeEndSecs ? 0 : null),
            start: res?.overrideRevealSyncDateStart ?? null,
            end: res?.overrideRevealSyncDateEnd ?? null,
            roundingMode: res?.overrideRevealSyncDateRoundingMode ?? null,
          });
        },
        () => {},
        () => {}
      );
    };

    fetchConfig();
  }, []);

  const onSave = async () => {
    if (!overrideDate) {
      return;
    }

    await tryInvokeApi(
      async () => {
        setIsLoading(true);
        await new SellerAccountClient(
          activeAccountWebClientConfig
        ).upsertRevealSyncConfig({
          syncDateOverride: overrideDate,
        });
      },
      (error) => {
        showErrorDialog('SellerAccountClient.upsertRevealSyncConfig', error, {
          trackErrorData: {
            overrideDate,
          },
        });
      },
      () => {
        setIsLoading(false);
      }
    );
  };

  return (
    <div>
      <div className={styles.sectionHeading}>
        <Content id={ContentId.RevealSyncDates} />
      </div>
      {!isFetching ? (
        <>
          <DateRangeSelector
            className={styles.dateRangeSelector}
            key="dateRangeSelector"
            useRelativePresets
            presetNames={StandardDateRangePresetNames}
            value={overrideDate}
            defaultValue={null}
            onChange={({ value }) => {
              const updatedvalue = value?.end
                ? {
                    ...value,
                    end: endOfDay(new Date(value.end)).toISOString(),
                  }
                : value;
              setOverrideDate(updatedvalue);
            }}
            displayTextStyle={{ maxWidth: '260px' }}
          />
          <Button
            className={styles.saveButton}
            onClick={onSave}
            disabled={isLoading}
          >
            <Content id={ContentId.Save} />
          </Button>
        </>
      ) : (
        <PosSpinner />
      )}
    </div>
  );
};
